import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as InfoOutlinedIcon,
  Build as BuildIcon,
  Public as PublicIcon,
  MailOutline as MailOutlineIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { getImageUrl } from '../utils/imageUtils';
import { images } from '../utils/imageUtils';

// Constants
const NAV_BG = '#181a20';
const NAV_TEXT = '#fff';
const NAV_HIGHLIGHT = '#c9a74c';
const NAV_FONT = 'Nunito, Montserrat, Segoe UI, Arial, sans-serif';

// Hero Header Components
const HeroHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  background: NAV_BG,
  color: '#ffffff',
  padding: theme.spacing(4, 0, 6, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'none',
    pointerEvents: 'none',
  },
}));

const HeroContent = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
}));

const HeroLogo = styled('img')(({ theme }) => ({
  height: 140,
  width: 'auto',
  borderRadius: 12,
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    height: 100,
    marginBottom: theme.spacing(2),
  },
}));

const HeroSearchBox = styled('form')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: 50,
  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  padding: '4px',
  minWidth: 400,
  maxWidth: 600,
  width: '90%',
  border: '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:focus-within': {
    border: '2px solid #c9a74c',
    boxShadow: '0 12px 40px rgba(201, 167, 76, 0.3)',
    transform: 'scale(1.02)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    width: '95%',
    borderRadius: 40,
  },
}));

const HeroSearchInput = styled('input')(({ theme }) => ({
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '1.1rem',
  fontFamily: NAV_FONT,
  padding: '12px 20px',
  width: '100%',
  borderRadius: 50,
  '&::placeholder': {
    color: '#666',
    opacity: 0.8,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 16px',
  },
}));

const FloatingLanguageButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#ffffff',
  width: 48,
  height: 48,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.1)',
  },
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(1),
    right: theme.spacing(1),
    width: 40,
    height: 40,
  },
}));

// Navigation Bar Components
const NavBar = styled(AppBar)(() => ({
  background: NAV_BG,
  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  zIndex: 1200,
}));

const NavMenu = styled(Box)(() => ({
  display: 'flex',
  gap: '2.5rem',
  alignItems: 'center',
  flexGrow: 1,
  justifyContent: 'center',
}));

const NavButton = styled(Button)(() => ({
  color: NAV_TEXT,
  fontFamily: NAV_FONT,
  fontWeight: 800,
  fontSize: '1.15rem',
  letterSpacing: '0.5px',
  background: 'none',
  borderRadius: 0,
  padding: '8px 0',
  minWidth: 0,
  borderBottom: '3px solid transparent',
  transition: 'color 0.2s, border-bottom 0.2s, text-shadow 0.2s',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  '&:hover, &.active': {
    color: NAV_HIGHLIGHT,
    borderBottom: `3px solid ${NAV_HIGHLIGHT}`,
    background: 'none',
    boxShadow: 'none',
    textShadow: '0 0 6px #c9a74c55',
  },
}));

const TopInfoBar = () => (
  <Box sx={{
    width: '100%',
    background: NAV_BG,
    color: '#fff',
    fontSize: { xs: '0.85rem', sm: '0.95rem' },
    py: { xs: 0.5, sm: 0.5 },
  }}>
    <Container sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0 }}>
      <Box>
        info@gobaadlogistics.com &nbsp; +202-812-2236
      </Box>
      <Box>
        <Link to="/newsletter" style={{ color: '#fff', textDecoration: 'none', fontWeight: 600, marginRight: 16 }}>Newsletter</Link>
        <Link to="/events" style={{ color: '#fff', textDecoration: 'none', fontWeight: 600 }}>Events</Link>
      </Box>
    </Container>
  </Box>
);

const Navbar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const navItems = [
    { key: 'home', label: t('home'), icon: <HomeIcon /> },
    { key: 'about', label: t('about.label'), icon: <InfoOutlinedIcon /> },
    { key: 'services', label: t('services.label'), icon: <BuildIcon /> },
    { key: 'routes', label: t('routes.label'), icon: <PublicIcon /> },
    { key: 'contact', label: t('contact.label'), icon: <MailOutlineIcon /> },
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (key) => {
    if (key === 'home') {
      navigate('/');
    } else {
      const element = document.getElementById(key);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    handleDrawerToggle();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/search?q=${encodeURIComponent(search)}`);
    }
  };

  return (
    <>
      <TopInfoBar />
      <HeroHeader>
        <Box sx={{
          position: 'absolute',
          top: theme.spacing(2),
          right: theme.spacing(2),
          zIndex: 2,
          [theme.breakpoints.down('sm')]: {
            top: theme.spacing(1),
            right: theme.spacing(1),
          },
        }}>
          <LanguageSwitcher />
        </Box>
        <HeroContent maxWidth="lg">
          <HeroLogo 
            src={getImageUrl(images.logoDark2)}
            alt="Gobaad Logistics Logo"
            onClick={() => navigate('/')}
            onError={(e) => {
              console.error('Error loading image:', e);
              e.target.onerror = null;
              e.target.src = getImageUrl(images.logoDark2);
            }}
          />
          <HeroSearchBox onSubmit={handleSearchSubmit}>
            <HeroSearchInput
              placeholder="Search for services, routes, or information..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <IconButton 
              type="submit"
              sx={{ 
                color: '#c9a74c', 
                fontSize: { xs: 20, sm: 24 }, 
                cursor: 'pointer',
                mr: 1,
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }} 
            >
              <SearchIcon />
            </IconButton>
          </HeroSearchBox>
        </HeroContent>
      </HeroHeader>
      <NavBar position="sticky">
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 56, px: { xs: 1, sm: 2 }, justifyContent: { xs: 'flex-end', sm: 'center' } }}>
            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  edge="end"
                  aria-label="menu"
                  onClick={handleDrawerToggle}
                  sx={{ ml: 'auto' }}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={handleDrawerToggle}
                  PaperProps={{ 
                    sx: { 
                      width: 260, 
                      pt: 2,
                      backgroundColor: NAV_BG,
                      color: NAV_TEXT
                    } 
                  }}
                >
                  <Box sx={{ width: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                    <HeroLogo 
                      src={getImageUrl(images.logoDark2)}
                      alt="Gobaad Logistics Logo"
                      style={{ marginBottom: 16, height: 80 }}
                    />
                    <Divider sx={{ width: '80%', mb: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <List sx={{ width: '100%' }}>
                      {navItems.map((item) => (
                        <ListItem 
                          button 
                          key={item.key}
                          onClick={() => handleNavigation(item.key)}
                          sx={{ 
                            py: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.1)',
                            }
                          }}
                        >
                          <ListItemIcon sx={{ color: NAV_HIGHLIGHT, minWidth: 40 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.label} 
                            primaryTypographyProps={{ 
                              fontSize: '1.15rem', 
                              fontWeight: 600,
                              color: NAV_TEXT
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ width: '80%', my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ width: '90%' }}>
                      <HeroSearchBox onSubmit={handleSearchSubmit} sx={{ width: '100%' }}>
                        <HeroSearchInput
                          placeholder="Search..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                        />
                        <IconButton type="submit" sx={{ color: '#c9a74c', fontSize: 22, cursor: 'pointer' }}>
                          <SearchIcon />
                        </IconButton>
                      </HeroSearchBox>
                    </Box>
                  </Box>
                </Drawer>
              </>
            ) : (
              <NavMenu>
                {navItems.map((item) => (
                  <NavButton 
                    key={item.key}
                    onClick={() => handleNavigation(item.key)}
                  >
                    {item.label}
                  </NavButton>
                ))}
              </NavMenu>
            )}
          </Toolbar>
        </Container>
      </NavBar>
    </>
  );
};

export default Navbar; 